<template>
  <div class="my-auto ml-auto mobile-filter mdsm:w-full">
    <div class="filter-buttons">
      <button type="button" class="btn btn-secondary btn-outlined lg:hidden sm:visible"
        @click="handleShowSort">
        <svg class="inline-block" width="24" height="24">
          <use xlink:href="/img/sprite.svg#ic_sort" />
        </svg>
        Sort
      </button>

      <button type="button" class="btn btn-secondary btn-outlined lg:hidden sm:visible"
        @click="handleShowView">
        <svg width="24" height="24" class="inline-block">
          <use xlink:href="/img/sprite.svg#ic_boat" />
        </svg>
        View
      </button>

      <button type="button" class="btn btn-secondary btn-outlined"
        @click="handleShowFilters">
        <svg class="inline-block" width="24" height="24">
          <use xlink:href="/img/sprite.svg#ic_filter" />
        </svg>
        Filter
      </button>
    </div>

    <zb-modal
      ref="filterModal"
      :is-full-screen="true"
      class="search-mobile-filter-modal"
      @open="$emit('modal-open')"
      @close="$emit('modal-close')"
    >
      <h2 slot="title" class="fs-20 font-medium mb-6">Filters</h2>

      <template #close>
        <div class="flex items-center filter-close">
          <svg width="24" height="24" class="block mr-2">
            <use xlink:href="/img/sprite.svg#ic_close"></use>
          </svg>
          <span class="font-medium">Close</span>
        </div>
      </template>

      <template #default>
        <div class="px-6 main-content">
          <div class="filter-container mx-auto">
            <availability-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <liveaboard-budget-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <trip-length-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <destination-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <tag-filter
              :tags="tags"
              @filter-changed="handleDoFiltering"
              class="my-6" />
            <liveaboard-dive-number-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <experiences-filter
              v-if="showExperiencesFilter"
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <rating-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
            <facilities-filter
              class="my-6"
              @filter-changed="handleDoFiltering" />
          </div>
        </div>
      </template>

      <template #stickyFooter>
        <footer class="px-6 py-4 text-right filter-footer flex gap-4">
          <button type="button"
            v-if="isShowingClearFilters"
            class="btn btn-secondary btn-outlined whitespace-no-wrap"
            @click="clearFilters">
            Clear filters
          </button>
          <button type="button"
            :disabled="isLoadingResults"
            class="btn btn-secondary sm:w-full whitespace-no-wrap"
            @click="submitFilters">
            {{ filterCTA }}
          </button>
        </footer>
      </template>
    </zb-modal>

    <zb-modal
      ref="sortModal"
      :is-full-screen="true"
      class="search-mobile-filter-modal lg:hidden sm:visible"
      @open="$emit('modal-open')"
      @close="$emit('modal-close')"
    >
      <h2 slot="title" class="fs-20 font-medium mb-6">Sort</h2>

      <template #close>
        <div class="flex items-center filter-close">
          <svg width="24" height="24" class="block mr-2">
            <use xlink:href="/img/sprite.svg#ic_close"></use>
          </svg>
          <span class="font-medium">Close</span>
        </div>
      </template>

      <template #default>
        <div class="px-6">
          <section class="mt-6">
            <button type="button"
              class="rank-button btn btn-secondary"
              :class="{
                'btn-outlined': ranking !== 'top-pick',
                'rank-button--active': ranking === 'top-pick'
              }"
              @click="setRanking('top-pick')">
              Our Top Picks
            </button>
            <button type="button"
              class="rank-button btn btn-secondary ml-4"
              :class="{
                'btn-outlined': ranking !== 'price',
                'rank-button--active': ranking === 'price'
              }"
              @click="setRanking('price')">
              Price (lowest first)
            </button>
            <button type="button"
              class="rank-button btn btn-secondary ml-4"
              :class="{
                'btn-outlined': ranking !== 'top-rated',
                'rank-button--active': ranking === 'top-rated'
              }"
              @click="setRanking('top-rated')">
              Top Rated
            </button>
            <button type="button"
              class="rank-button rank-special-offers btn btn-secondary ml-4"
              :class="{
                'btn-outlined': ranking !== 'special-offers',
                'rank-button--active': ranking === 'special-offers'
              }"
              @click="setRanking('special-offers')">
              Special Offers
            </button>
          </section>
        </div>
      </template>

      <template #stickyFooter>
        <footer class="px-6 py-4 text-right filter-footer">
          <button type="button" class="btn btn-secondary sm:w-full" @click="submitSort">
            Sort results
          </button>
        </footer>
      </template>
    </zb-modal>

    <zb-modal
      ref="viewModal"
      :is-full-screen="true"
      class="search-mobile-filter-modal lg:hidden sm:visible"
      @open="$emit('modal-open')"
      @close="$emit('modal-close')"
    >
      <h2 slot="title" class="fs-20 font-medium mb-6">View</h2>

      <template #close>
        <div class="flex items-center filter-close">
          <svg width="24" height="24" class="block mr-2">
            <use xlink:href="/img/sprite.svg#ic_close"></use>
          </svg>
          <span class="font-medium">Close</span>
        </div>
      </template>

      <template #default>
        <div class="px-6">
          <section class="mt-6">
            <button type="button"
              class="rank-button btn btn-secondary"
              :class="{
                'btn-outlined': view !== 'boats',
                'rank-button--active': view === 'boats'
              }"
              @click="setViewBy('boats')">
              Boats
            </button>
            <button type="button"
              class="rank-button btn btn-secondary ml-4"
              :class="{
                'btn-outlined': view !== 'dates',
                'rank-button--active': view === 'dates'
              }"
              @click="setViewBy('dates')">
              Departure date
            </button>
          </section>
        </div>
      </template>

      <template #stickyFooter>
        <footer class="px-6 py-4 text-right filter-footer">
          <button type="button" class="btn btn-secondary sm:w-full" @click="submitView">
            View results
          </button>
        </footer>
      </template>
    </zb-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import flatMap from 'lodash/flatMap'
import uniqBy from 'lodash/uniqBy'
import orderBy from 'lodash/orderBy'
import get from 'lodash/get'

import Modal from '../../core/ZbModal'
import DestinationFilter from './DestinationFilter'
import ExperiencesFilter from './ExperiencesFilter'
import RatingFilter from './RatingFilter'
import FacilitiesFilter from './FacilitiesFilter'
import TripLengthFilter from './TripLengthFilter'
import LiveaboardBudgetFilter from './LiveaboardBudgetFilter'
import LiveaboardDiveNumberFilter from './LiveaboardDiveNumberFilter'
import TagFilter from './TagFilter'
import AvailabilityFilter from './AvailabilityFilter'
import {
  allDestinationItems
} from '../../../helpers/searchHelpers'

export default {
  components: {
    LiveaboardBudgetFilter,
    DestinationFilter,
    RatingFilter,
    FacilitiesFilter,
    ExperiencesFilter,
    TripLengthFilter,
    LiveaboardDiveNumberFilter,
    TagFilter,
    AvailabilityFilter,
    'zb-modal': Modal
  },
  provide () {
    return {
      options: this.options
    }
  },
  data () {
    return {
      tags: [],
      isLoadingResults: false,
      options: {
        budgetOptions: this.getLiveaboardBudgetOptions(),
        destinationOptions: this.getDestinationOptions(),
        ratingOptions: this.getRatingOptions(),
        facilitiesOptions: this.getFacilitiesOptions(),
        speciesOptions: this.getSpeciesOptions(),
        tripLengthOptions: this.getTripLengthOptions(),
        numDivesOptions: this.getNumDivesOptions(),
        availabilityOptions: this.getAvailabilityOptions(),
        tagOptions: []
      }
    }
  },
  computed: {
    ...mapState(['resortsResult', 'ranking', 'filters', 'view']),
    ...mapGetters(['experienceList', 'showExperiencesFilter', 'sortedResults', 'allTrips']),
    filterCTA () {
      let length = 0;
      if (this.view === 'boats'){
        length = this.sortedResults.length
      } else {
        length = this.allTrips.length
      }

      if (length <= 1) {
        return `Show ${length} result`
      }
      else {
        return `Show ${length} results`
      }
    },
    isShowingClearFilters () {
      return get(this.filters, ['budgets'], '') !== '' ||
        get(this.filters, ['categories'], '') !== '' ||
        get(this.filters, ['destinations'], '') !== '' ||
        get(this.filters, ['experiences'], '') !== '' ||
        get(this.filters, ['facilities'], '') !== '' ||
        get(this.filters, ['greenfin'], '') !== '' ||
        get(this.filters, ['rating'], '') !== '' ||
        get(this.filters, ['species'], '') !== '' ||
        get(this.filters, ['tripLengths'], '') !== '' ||
        get(this.filters, ['availability'], '') !== '' ||
        get(this.filters, ['tags'], '') !== ''
    }
  },
  mounted () {
    this.$store.dispatch('loadDestinations')

    $.ajax({
      method: 'POST',
      url: '/',
      data: {
        action: 'zuBlu/public/getSearchTags',
        [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
      },
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).then((response) => {
      this.tags = JSON.parse(response);
      this.options.tagOptions =  this.getTagOptions()
    });
  },
  methods: {
    ...mapMutations(['setRanking', 'setPage', "setView"]),
    handleShowFilters () {
      this.$refs.filterModal.show()
    },
    handleShowSort () {
      this.$refs.sortModal.show()
    },
    handleShowView () {
      this.$refs.viewModal.show()
    },
    getLiveaboardBudgetOptions () {
      const filters = this.$store.state.filters
      const budgetValues = filters.budgets.split(',')

      const options = {
        min0: budgetValues.includes('0'),
        min1000: budgetValues.includes('1000'),
        min2000: budgetValues.includes('2000'),
        min3000: budgetValues.includes('3000'),
        min4000: budgetValues.includes('4000'),
        min5000: budgetValues.includes('5000')
      }

      return options
    },
    getDestinationOptions () {
      const { filters } = this.$store.state
      const selectingDestinations = filters.destinations.split(',')

      const options = allDestinationItems
        .map(entry => entry.id)
        .reduce((obj, id) => {
          const isSelected = selectingDestinations.includes(id)

          return Object.assign({ [`opt${id}`]: isSelected }, obj)
        }, {})

      return options
    },
    getRatingOptions () {
      const filters = this.$store.state.filters
      const ratingValues = filters.rating.split(',')

      const options = {
        excellent: ratingValues.includes('excellent'),
        very_good: ratingValues.includes('very_good'),
        average: ratingValues.includes('average'),
        poor: ratingValues.includes('poor'),
        unrated: ratingValues.includes('unrated')
      }

      return options
    },
    getFacilitiesOptions () {
      const { filters, resortsResult } = this.$store.state
      const selectingFacilities = filters.facilities.split(',')

      let allFacilities = flatMap(resortsResult, resort => {
        return get(resort, ['inclusions_objects'], [])
      })
      allFacilities = uniqBy(allFacilities, item => item.name)
      allFacilities = orderBy(allFacilities, ['order', 'name'], ['asc', 'asc'])

      const options = allFacilities
        .map(item => item.slug)
        .reduce((obj, slug) => {
          const isSelected = selectingFacilities.includes(slug)

          return Object.assign({ [`opt${slug}`]: isSelected }, obj)
        }, {})

      return options
    },
    getSpeciesOptions () {
      const { filters } = this.$store.state
      const selectedSpecies = filters.species.split(',')
        .filter(x => x)

      const options = (this.experienceList || []).reduce((opts, exp) => {
        return Object.assign(opts, {
          [`opt${exp.id}`]: selectedSpecies.includes(exp.id.toString())
        })
      }, {})

      return options
    },
    getTripLengthOptions () {
      const filters = this.$store.state.filters
      const lengthValues = filters.tripLengths.split(',')

      const options = {
        min0: lengthValues.includes('0'),
        min5: lengthValues.includes('5'),
        min8: lengthValues.includes('8')
      }

      return options
    },
    getAvailabilityOptions () {
      const filters = this.$store.state.filters
      const availabilityValues = filters.availability.split(',')

      const options = {
        "special-offers": availabilityValues.includes('special-offers'),
        "available-trips": availabilityValues.includes('available-trips')
      }

      return options
    },
    getNumDivesOptions () {
      const filters = this.$store.state.filters
      const numDivesValues = filters.numDives.split(',')

      const options = {
        min5: numDivesValues.includes('5'),
        min31: numDivesValues.includes('31'),
        min51: numDivesValues.includes('51'),
        min101: numDivesValues.includes('101')
      }

      return options
    },
    getTagOptions () {
      const { filters } = this.$store.state
      const selectedTags = filters.tags.split(',')
        .map(x => x)
        .filter(x => x)

      const options = this.tags.reduce((opts, tag) => {
        return Object.assign(opts, {
          [`opt${tag.value}`]: selectedTags.includes(tag.value.toString()),
        });
      }, {});

      return options;
    },
    handleDoFiltering () {
      if (this.isLoadingResults === true) {
        return
      }

      this.isLoadingResults = true

      const budgets = Object.keys(this.options.budgetOptions).reduce((list, key) => {
        if (this.options.budgetOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const destinations = Object.keys(this.options.destinationOptions).reduce((list, key) => {
        if (this.options.destinationOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const rating = Object.keys(this.options.ratingOptions).reduce((list, key) => {
        if (this.options.ratingOptions[key]) {
          list.push(key)
        }

        return list
      }, []).join(',')

      const facilities = Object.keys(this.options.facilitiesOptions).reduce((list, key) => {
        if (this.options.facilitiesOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const species = Object.keys(this.options.speciesOptions).reduce((list, key) => {
        if (this.options.speciesOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const tripLengths = Object.keys(this.options.tripLengthOptions).reduce((list, key) => {
        if (this.options.tripLengthOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const availability = Object.keys(this.options.availabilityOptions).reduce((list, key) => {
        if (this.options.availabilityOptions[key]) {
          list.push(key)
        }

        return list
      }, []).join(',')

      const numDives = Object.keys(this.options.numDivesOptions).reduce((list, key) => {
        if (this.options.numDivesOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const tags = Object.keys(this.options.tagOptions).reduce((list, key) => {
        if (this.options.tagOptions[key]) {
          list.push(key.substring(3))
        }

        return list
      }, []).join(',')

      const newFilters = {
        budgets,
        destinations,
        rating,
        facilities,
        species,
        tripLengths,
        availability,
        numDives,
        tags
      }

      this.$store.commit('setFilters', newFilters)
      this.$store.dispatch('loadSuppliers')
      .then(() => {
        this.isLoadingResults = false
      })
    },
    submitFilters () {
      this.$refs.filterModal.hide()
    },
    submitSort () {
      this.setPage(1)
      this.$refs.sortModal.hide()
    },
    submitView () {
      this.setPage(1)
      this.$refs.viewModal.hide()
    },
    clearFilters() {
      const newFilters = {
        budgets: '',
        categories: '',
        destinations: '',
        experiences: '',
        facilities: '',
        greenfin: '',
        rating: '',
        species: '',
        tripLengths: '',
        availability: '',
        numDives: '',
        tags: '',
      }

      for (const optGroup in this.options) {
        for (const opt in this.options[optGroup]) {
          this.options[optGroup][opt] = false
        }
      }

      this.$store.commit('setFilters', newFilters)
      this.$store.dispatch('loadSuppliers')
    },
    setViewBy (val) {
      if(val === 'dates'){
        this.setRanking('price')
      }
      this.setView(val)
    }
  },
  watch: {
    resortsResult () {
      this.options.facilitiesOptions = this.getFacilitiesOptions()
    },
    experienceList () {
      this.options.speciesOptions = this.getSpeciesOptions()
    }
  }
}
</script>

<style scoped>
.filter-close {
  fill: var(--secondary);
  color: var(--secondary);
}

.filter-buttons svg {
  fill: var(--secondary);
}

.mobile-filter ::v-deep label {
  margin-bottom: 0.5rem;
}

.filter-container {
  max-width: 375px;
  width: 100%;
}

.filter-footer {
  border-top: 1px solid var(--border-primary);
}

.main-content {
  padding-left: 24px;
  padding-right: 24px;
}

@media (max-width: 600px) {
  .filter-close {
    flex-direction: row-reverse;
  }

  .filter-close svg {
    margin-right: 0;
    margin-left: 0.5rem;
  }

  .filter-container {
    max-width: unset;
    width: 100%;
    background-color: var(--black-light-1);
  }

  .filter-container > section {
    background-color: white;
    padding: 24px;
    margin-top: 8px;
    margin-bottom: 0px;
  }

  .rank-button {
    display: block;
    margin-left: 0;
    margin-top: 1rem;
  }

  .main-content {
    padding-left: 0px;
    padding-right: 0px;
  }
}

@media (max-width: 1200px) {
 .filter-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 16px;
  }
}
</style>
